import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

import type { parentIssueField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/parentIssueField_updateWorkItemAllFieldTypesActionRenderer.graphql';
import { ParentIssueFieldSkeleton } from './ParentIssueFieldSkeleton';
import { useHydratedIssue } from './useHydratedIssue';

const messages = defineMessages({
	addToParentWorkItem: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.parent-issue-field.addToParentWorkItem',
		defaultMessage: 'Add to parent work item',
		description:
			'Text shown on the button or option to assign the current work item to a parent work item in a Jira action card.',
	},
	addParentWorkItem: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.parent-issue-field.addParentWorkItem.non-final',
		defaultMessage: 'Add parent work item: {parentIssue}',
		description:
			'Text shown on the button or option to assign the current work item to a parent work item, including the suggested parent work item key and summary.',
	},
	unassign: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.parent-issue-field.unassign',
		defaultMessage: 'Unassign parent',
		description:
			'Text shown on the button or option to remove the parent work item assignment from the current Jira work item in an action card.',
	},
});

export type Props = {
	cloudId: string;
	queryRef: parentIssueField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
	onInvalidAction?: () => void;
};

export const ParentIssueField = ({
	cloudId,
	queryRef,
	action,
	registerTooltipProvider,
	onInvalidAction,
}: Props): React.JSX.Element | null => {
	di(useHydratedIssue);

	const { formatMessage } = useIntl();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment parentIssueField_updateWorkItemAllFieldTypesActionRenderer on JiraParentIssueField {
				fieldId
				name
				parentIssue {
					issueId
					key
					summary
				}
			}
		`,
		queryRef,
	);

	const proposedParentId =
		typeof fieldValue === 'string' ? fieldValue.trim() : fieldValue?.toString() || null;

	const isFieldRenderable = data?.fieldId === 'parent' && data.fieldId === fieldKey;

	const needsHydration = Boolean(
		isFieldRenderable && proposedParentId && proposedParentId !== data?.parentIssue?.issueId,
	);

	const hydratedData = useHydratedIssue(cloudId, proposedParentId, needsHydration, onInvalidAction);

	const parentIssue =
		needsHydration && hydratedData
			? { issue: hydratedData.issue, isLoading: hydratedData.isLoading, error: hydratedData.error }
			: { issue: data?.parentIssue, isLoading: false, error: null };

	const { issue, isLoading, error } = parentIssue;

	const isAssignable = (issue || isLoading) && proposedParentId;
	const isNotFound = !issue && !isLoading && proposedParentId;

	const formattedMessage = isAssignable
		? FeatureGates.getExperimentValue(
				'jira-ai-update-workitem-field-action-details-exp',
				'isEnabled',
				false,
			) && issue?.key
			? formatMessage(messages.addParentWorkItem, {
					parentIssue: issue.summary ? `${issue.key} ${issue.summary}` : issue.key,
				})
			: formatMessage(messages.addToParentWorkItem)
		: formatMessage(messages.unassign);

	// Compute tooltip content provider logic
	const toolTipContentProvider = useCallback(() => {
		if (!isFieldRenderable) {
			return null;
		}
		return (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				current={
					data?.parentIssue ? `${data.parentIssue.key}: ${data.parentIssue.summary}` : undefined
				}
				suggested={issue ? `${issue?.key}: ${issue?.summary}` : undefined}
				fieldName={data?.name}
			/>
		);
	}, [fieldReasoning, data.parentIssue, data?.name, isFieldRenderable, issue]);

	// Register tooltip content provider
	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!isFieldRenderable || isNotFound || error) {
		return null;
	}

	if (isLoading) {
		return <ParentIssueFieldSkeleton />;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.parent-issue-field"
		>
			{formattedMessage}
		</Text>
	);
};
